// Shared pieces of the Live views (Live Orders, Community Live Activity).
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./live.css";
import { elakayAutoText, elakayIntlLocale } from "../../pageInside/dataElakay";
import { roleDisplayLabel } from "../../../utils/roleDisplayLabel";
import { formatElakayTime } from "../../../utils/elakayDate";


export const ROLE_KEYS = ["user", "royalty", "seller", "provider", "associate", "investment", "administrator", "family"];
// Role names from the shared Elakay role vocabulary (utils/roleDisplayLabel), in the active language.
export const ROLE_LABELS = Object.fromEntries([...ROLE_KEYS, "unknown"].map((key) => [key, null]));
Object.keys(ROLE_LABELS).forEach((key) => Object.defineProperty(ROLE_LABELS, key, { get: () => roleDisplayLabel(key), enumerable: true }));

export const formatCount = (n) => new Intl.NumberFormat(elakayIntlLocale()).format(Number(n) || 0);
export const formatMoney = (cents) => new Intl.NumberFormat(elakayIntlLocale(), { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
export const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(elakayIntlLocale(), { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// Only claims "LIVE" while a stream is really delivering updates.
export const LiveIndicator = ({ status, t }) => {
  const map = {
    live: ["is-live", t("live", "LIVE")],
    connecting: ["is-wait", t("connecting", "Connecting…")],
    reconnecting: ["is-wait", t("reconnecting", "Reconnecting…")],
    paused: ["is-paused", t("paused", "Paused (tab hidden)")],
    error: ["is-error", t("liveError", "Not live")],
  };
  const [cls, label] = map[status] || ["is-off", t("off", "Not live")];
  return <span className={`live-indicator ${cls}`} role="status"><i aria-hidden="true" />{label}</span>;
};

export const LastUpdated = ({ at, t }) => (
  <span className="live-muted">{t("lastUpdated", "Last updated")}: {at ? formatElakayTime(at) : "—"}</span>
);

// Big number that briefly highlights when it goes up (subtle, no motion if reduced).
export const LiveNumber = ({ value, className = "" }) => {
  const prev = useRef(value);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (typeof value === "number" && typeof prev.current === "number" && value > prev.current) setBump((b) => b + 1);
    prev.current = value;
  }, [value]);
  return <strong key={bump} className={`live-number ${bump ? "is-bumped" : ""} ${className}`}>{value == null ? "—" : formatCount(value)}</strong>;
};

const pad = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

/**
 * Start Date + Start Time picker. Calls onStart(Date) with the instant in the
 * viewer's local time zone.
 */
export const SinceModal = ({ show, title, initial, onStart, onHide, t }) => {
  const base = initial ? new Date(initial) : startOfToday();
  const [date, setDate] = useState(localDate(base));
  const [time, setTime] = useState(localTime(base));
  const [error, setError] = useState("");
  // Which quick preset produced the current date/time (cleared on manual edit).
  const [activePreset, setActivePreset] = useState(initial ? null : "today");
  useEffect(() => {
    if (!show) return;
    const b = initial ? new Date(initial) : startOfToday();
    setDate(localDate(b)); setTime(localTime(b)); setError(""); setActivePreset(initial ? null : "today");
  }, [show, initial]);
  const submit = (e) => {
    e.preventDefault();
    const at = new Date(`${date}T${time || "00:00"}`);
    if (Number.isNaN(at.getTime())) { setError(t("sinceInvalid", elakayAutoText("h_fa8376f1eb926a", "Choose a valid date and time."))); return; }
    if (at.getTime() > Date.now()) { setError(t("sinceFuture", elakayAutoText("h_f3e9d89faf9512", "The start must be in the past."))); return; }
    onStart(at);
  };
  const apply = (key, d) => { setDate(localDate(d)); setTime(localTime(d)); setActivePreset(key); setError(""); };
  const preset = (key, hoursBack) => apply(key, new Date(Date.now() - hoursBack * 3600 * 1000));
  const presetButton = (key, label, onClick) => (
    <button type="button" aria-pressed={activePreset === key ? "true" : "false"} onClick={onClick}>{label}</button>
  );
  return (
    <Modal show={show} onHide={onHide} centered className="live-modal" aria-labelledby="live-since-title">
      <form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title as="h2" id="live-since-title" className="live-modal-title">{title}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="live-muted">{t("sinceHelp", "Counts everything created from this moment, then keeps counting live while this view stays open.")}</p>
          <div className="live-since-grid">
            <label htmlFor="live-since-date"><span>{t("startDate", "Start date")}</span>
              <input id="live-since-date" type="date" value={date} max={localDate(new Date())} onChange={(e) => { setDate(e.target.value); setActivePreset(null); }} required />
            </label>
            <label htmlFor="live-since-time"><span>{t("startTime", "Start time")}</span>
              <input id="live-since-time" type="time" value={time} onChange={(e) => { setTime(e.target.value); setActivePreset(null); }} required />
            </label>
          </div>
          <div className="live-presets" role="group" aria-label={t("quickStart", "Quick start")}>
            {presetButton("today", t("presetToday", "Today 12:00 AM"), () => apply("today", startOfToday()))}
            {presetButton("hour", t("presetHour", "1 hour ago"), () => preset("hour", 1))}
            {presetButton("day", t("preset24", "24 hours ago"), () => preset("day", 24))}
            {presetButton("week", t("preset7d", "7 days ago"), () => preset("week", 24 * 7))}
          </div>
          {error && <p className="live-error" role="alert">{error}</p>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="live-btn live-btn-ghost" onClick={onHide}>{t("cancel", "Cancel")}</button>
          <button type="submit" className="live-btn">{t("startLive", "START LIVE")}</button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
